import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Armchair, Utensils, Trees, Bed, Sparkles } from "lucide-react";
import { facilities, amenities } from "../mock";

export const Facilities = () => {
  return (
    <section id="facilities" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-orange-100 text-orange-700 hover:bg-orange-200">
            Our Facilities
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Designed to Feel Like Home
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comfortable, accessible spaces created for wellbeing, not an institution
          </p>
        </div>

        {/* Facilities Grid with Images */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {facilities.map((facility, index) => (
            <Card
              key={index}
              className="border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={facility.image}
                  alt={facility.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <CardContent className="pt-4 pb-6">
                <h3 className="font-bold text-lg text-gray-900 mb-2">{facility.title}</h3>
                <p className="text-gray-600 text-sm">{facility.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Amenities List */}
        <div className="max-w-6xl mx-auto">
          <Card className="border-2 border-teal-200 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center bg-teal-100 text-teal-700 p-4 rounded-full mb-4">
                  <Sparkles className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Complete Amenities & Services
                </h3>
                <p className="text-gray-600">
                  Everything you need for comfort, dignity, and quality of life
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {amenities.map((amenity, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-teal-50 transition-colors"
                  >
                    <div className="bg-orange-500 text-white p-1.5 rounded-full flex-shrink-0 mt-0.5">
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{amenity}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Special Features */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
            <CardContent className="pt-6 text-center">
              <div className="bg-white text-teal-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Utensils className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Home-Cooked Meals</h3>
              <p className="text-gray-700 text-sm">
                Freshly prepared nutritious meals with choice of menu and special dietary requirements catered for
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="pt-6 text-center">
              <div className="bg-white text-orange-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Trees className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Beautiful Gardens</h3>
              <p className="text-gray-700 text-sm">
                Landscaped outdoor spaces for residents to enjoy fresh air, nature, and gentle activities
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="pt-6 text-center">
              <div className="bg-white text-blue-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Activity Programme</h3>
              <p className="text-gray-700 text-sm">
                Daily activities, exercise sessions, and day trips designed to promote wellbeing and engagement
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
